import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import Button from '../../../../components/common/Button/Button';
import DurationPickerField from '../../../../components/common/DurationPickerField/DurationPickerField';
import ExerciseThumbnailImage from '../../../../components/common/ExerciseThumbnailImage/ExerciseThumbnailImage';
import Icon from '../../../../components/common/Icon/Icon';
import SearchableSelect from '../../../../components/common/SearchableSelect/SearchableSelect';
import type { ExerciseItem } from '../../../../types/exercise';
import {
  TRAINING_EXERCISE_GROUPING_COLORS,
  TRAINING_EXERCISE_GROUPING_LABELS,
  isTabataSupportedTrainingGroupingType,
  type TrainingExerciseExecution,
  type TrainingExerciseExecutionDetail,
  type TrainingExerciseGroupingType,
  type TrainingExerciseMethodConfig,
} from '../../../../types/training';
import {
  getCanonicalTrainingExecutionMethodValue,
  getTrainingExecutionMethodDefinition,
  getTrainingExecutionMethodDisplayLabel,
  isClusterSetTrainingExecutionMethod,
  isDropFamilyTrainingExecutionMethod,
  isGvtTrainingExecutionMethod,
  isPyramidTrainingExecutionMethod,
  isRestPauseTrainingExecutionMethod,
  isTabataTrainingExecutionMethod,
  usesManualLoadPercentage,
  usesStructureFieldForTrainingExecutionMethod,
} from '../../../../utils/trainingExecutionMethods';
import {
  getPyramidLoadPercentageDisplay,
  getPyramidLoadPercentageValue,
} from '../../../../utils/pyramidLoadPercentage';
import {
  DEFAULT_CONCENTRIC_SECONDS,
  DEFAULT_ECCENTRIC_SECONDS,
  DEFAULT_PAUSE_SECONDS,
  DEFAULT_REST_SECONDS,
  EXERCISE_EXECUTION_SPEED_OPTIONS,
  buildExecutionSpeedFields,
  isCadenceExecutionSpeed,
  isIsometryExecutionSpeed,
  normalizeExecutionSpeed,
  type ExerciseExecutionSpeed,
} from '../../../../utils/executionSpeed';
import {
  EXERCISE_REPETITION_SCOPE_OPTIONS,
  normalizeRepetitionScope,
  type ExerciseRepetitionScope,
} from '../../../../utils/repetitionScope';
import ExerciseSwapModal from '../ExerciseSwapModal';
import ExerciseVideoModal from '../ExerciseVideoModal';
import NumericStepperInput from './NumericStepperInput';
import { useEscapeToClose } from '../../../../hooks/useEscapeToClose';
import styles from './ExerciseExecutionEditor.module.css';

const POPOVER_WIDTH = 420;
const POPOVER_GAP = 10;
const ARROW_HEIGHT = 8;
const VIEWPORT_MARGIN = 12;

type NumericExecutionField = 'sets' | 'repetitions' | 'rest' | 'details' | 'roundRepetitions' | 'intraSetRest' | 'executionSeconds' | 'isometricSeconds' | 'concentricSeconds' | 'pauseSeconds' | 'eccentricSeconds';
type ExecutionDetailField = 'repetitionsMin' | 'repetitionsMax' | 'loadPercentage' | 'block';

type ExecutionDetailDraft = {
  id: string;
  repetitionsMin: string;
  repetitionsMax: string;
  loadPercentage: string;
  rest: string;
  block: string;
};

type ExecutionMethodConfigDraft = {
  roundRepetitions?: string;
  intraSetRest?: string;
  fixedRestMin?: number | null;
  fixedRestMax?: number | null;
};

type ExecutionDraft = {
  sets: string;
  repetitions: string;
  rest: string;
  method: string;
  details: ExecutionDetailDraft[];
  persistedSetIds?: number[];
  methodConfig?: ExecutionMethodConfigDraft | null;
  repetitionScope: ExerciseRepetitionScope;
  executionSpeed?: ExerciseExecutionSpeed | null;
  isometricSeconds?: string;
  concentricSeconds?: string;
  pauseSeconds?: string;
  eccentricSeconds?: string;
  executionSeconds?: TrainingExerciseExecution['executionSeconds'];
  exerciseType?: TrainingExerciseExecution['exerciseType'];
};

interface ExecutionMethodOption {
  value: string;
  label: string;
  group?: string;
  color?: string | null;
  disabled?: boolean;
}

interface SelectedExercisePreview {
  id: string;
  blockId: string;
  catalogExerciseId: number;
  name: string;
}

interface ExerciseExecutionEditorProps {
  execution: TrainingExerciseExecution;
  exerciseName: string;
  catalogExerciseId: number;
  structureType: TrainingExerciseGroupingType;
  anchorRect: DOMRect;
  methodOptions: ExecutionMethodOption[];
  isReadOnly?: boolean;
  isTabataController?: boolean;
  tabataControllerName?: string | null;
  selectedExercises?: SelectedExercisePreview[];
  onConfirm: (execution: TrainingExerciseExecution) => void;
  onClose: () => void;
  onReplaceExercise?: (
    nextExercise: ExerciseItem,
    replaceInEntirePlan: boolean,
    target: { blockId: string; exerciseId: string },
  ) => void;
}

const DEFAULT_NUMERIC_VALUE = '1';
const DEFAULT_REPETITIONS_VALUE = '1';
const DEFAULT_REST_VALUE = String(DEFAULT_REST_SECONDS);
const DEFAULT_GVT_REST_LABEL = '30-60 segundos';
const DEFAULT_TABATA_EXECUTION_SECONDS = 20;
const DEFAULT_TABATA_REST_VALUE = '10';

const normalizePositiveIntegerInput = (value: string, maxDigits?: number) => {
  const digits = value.replace(/\D/g, '');
  const normalizedValue = digits.replace(/^0+/, '');

  if (!normalizedValue) {
    return '';
  }

  return typeof maxDigits === 'number'
    ? normalizedValue.slice(0, maxDigits)
    : normalizedValue;
};

const normalizePositiveIntegerNumber = (value?: string | number | null, maxDigits?: number) => {
  const normalizedValue = normalizePositiveIntegerInput(String(value ?? ''), maxDigits);

  if (!normalizedValue) {
    return null;
  }

  return Number.parseInt(normalizedValue, 10);
};

const normalizeNonNegativeIntegerNumber = (value?: string | number | null, maxDigits?: number) => {
  const trimmedValue = String(value ?? '').trim();

  if (trimmedValue === '0') {
    return 0;
  }

  return normalizePositiveIntegerNumber(value, maxDigits);
};

const normalizeLoadPercentageNumber = (value?: string | number | null) => {
  const digits = String(value ?? '').replace(/\D/g, '').slice(0, 3);

  if (!digits) {
    return null;
  }

  const parsedValue = Number.parseInt(digits, 10);

  if (!Number.isInteger(parsedValue) || parsedValue < 0 || parsedValue > 100) {
    return null;
  }

  return parsedValue;
};

const formatOptionalInteger = (value?: number | null) => (
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? String(Math.trunc(value)) : ''
);

const normalizeLoadPercentageInput = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 3);

  if (!digits) {
    return '';
  }

  return String(Number.parseInt(digits, 10));
};

const parseRepetitionRangeValue = (value?: string | null) => {
  const trimmedValue = value?.trim() ?? '';

  if (!trimmedValue) {
    return null;
  }

  const rangeMatch = trimmedValue.match(/^(\d*)(?:-(\d*))?$/);

  if (!rangeMatch) {
    return null;
  }

  const normalizedMin = normalizePositiveIntegerInput(rangeMatch[1] ?? '', 3);
  const hasExplicitRange = typeof rangeMatch[2] === 'string';
  const normalizedMax = normalizePositiveIntegerInput(rangeMatch[2] ?? '', 3);

  if (!normalizedMin && !normalizedMax) {
    return null;
  }

  return {
    min: normalizedMin,
    max: normalizedMax,
    isExplicitRange: hasExplicitRange,
  };
};

const buildRangeValue = (minValue: string, maxValue: string, forceExplicitRange = false) => {
  const normalizedMin = normalizePositiveIntegerInput(minValue, 3);
  const normalizedMax = normalizePositiveIntegerInput(maxValue, 3);

  if (!normalizedMin && !normalizedMax) {
    return '';
  }

  if (forceExplicitRange) {
    return `${normalizedMin}-${normalizedMax}`;
  }

  if (!normalizedMin) {
    return normalizedMax;
  }

  if (!normalizedMax) {
    return normalizedMin;
  }

  return normalizedMin === normalizedMax
    ? normalizedMin
    : `${normalizedMin}-${normalizedMax}`;
};

const isPositiveIntegerValue = (value: string) => /^[1-9]\d*$/.test(value.trim());
const isNonNegativeIntegerValue = (value: string) => /^(0|[1-9]\d*)$/.test(value.trim());

const isValidLoadPercentageValue = (value: string) => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return true;
  }

  const parsedValue = Number.parseInt(trimmedValue, 10);

  return Number.isInteger(parsedValue) && parsedValue >= 0 && parsedValue <= 100;
};

const isValidRepetitionValue = (value: string, requireExplicitRange = false) => {
  if (!requireExplicitRange) {
    return isPositiveIntegerValue(value);
  }

  const parsedRange = parseRepetitionRangeValue(value);

  if (!parsedRange) {
    return false;
  }

  return !requireExplicitRange || parsedRange.isExplicitRange;
};

const createExecutionDetailId = () => `execution-detail-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const resolveSingleRepetitionValue = (value?: string | null) => {
  const parsedRange = parseRepetitionRangeValue(value);

  if (parsedRange) {
    return parsedRange.max || parsedRange.min;
  }

  return normalizePositiveIntegerInput(value ?? '', 3);
};

const buildRestSummaryFromDetails = (details: ExecutionDetailDraft[]) => details.find((detail) => detail.rest.trim())?.rest ?? '';

const getNextSequentialBlockValue = (details: ExecutionDetailDraft[]) => {
  const blockValues = details
    .map((detail) => Number.parseInt(detail.block, 10))
    .filter((value): value is number => Number.isInteger(value) && value > 0);

  return String((blockValues.length > 0 ? Math.max(...blockValues) : 0) + 1);
};

const buildRepetitionsSummaryFromDetails = (details: ExecutionDetailDraft[]) => {
  const repetitionValues = details.flatMap((detail) => {
    const minValue = Number.parseInt(detail.repetitionsMin, 10);
    const maxValue = Number.parseInt(detail.repetitionsMax, 10);

    return [minValue, maxValue].filter((value): value is number => value !== null && Number.isInteger(value) && value > 0);
  });

  if (repetitionValues.length === 0) {
    return '';
  }

  const minValue = Math.min(...repetitionValues);
  const maxValue = Math.max(...repetitionValues);

  return minValue === maxValue ? String(minValue) : `${minValue}-${maxValue}`;
};

const createDropFamilyExecutionDetail = (
  detail: Partial<ExecutionDetailDraft> | undefined,
  index: number,
  fallbackRepetitions = DEFAULT_REPETITIONS_VALUE,
): ExecutionDetailDraft => {
  const resolvedRepetitions = resolveSingleRepetitionValue(
    detail?.repetitionsMax || detail?.repetitionsMin || fallbackRepetitions,
  ) || DEFAULT_REPETITIONS_VALUE;

  return {
    id: detail?.id?.trim() || createExecutionDetailId(),
    repetitionsMin: resolvedRepetitions,
    repetitionsMax: resolvedRepetitions,
    loadPercentage: normalizeLoadPercentageInput(detail?.loadPercentage ?? ''),
    rest: '',
    block: normalizePositiveIntegerInput(detail?.block ?? '', 3) || String(index + 1),
  };
};

const normalizeDropFamilyDetails = (
  details: ExecutionDetailDraft[],
  fallbackRepetitions = DEFAULT_REPETITIONS_VALUE,
  collapseToSingleDetail = false,
): ExecutionDetailDraft[] => {
  const sourceDetails = collapseToSingleDetail && details.length > 0
    ? [details[0]]
    : details;

  if (sourceDetails.length === 0) {
    return [createDropFamilyExecutionDetail(undefined, 0, fallbackRepetitions)];
  }

  return sourceDetails.map((detail, index) => createDropFamilyExecutionDetail(detail, index, fallbackRepetitions));
};

const applyPyramidLoadToDetail = (detail: ExecutionDetailDraft): ExecutionDetailDraft => ({
  ...detail,
  loadPercentage: getPyramidLoadPercentageDisplay(detail.repetitionsMax || detail.repetitionsMin),
});

const resolvePyramidSetCount = (sets: string) => {
  const parsedValue = Number.parseInt(sets, 10);

  return Number.isInteger(parsedValue) && parsedValue > 0 ? Math.min(parsedValue, 99) : 1;
};

const syncPyramidDetails = (
  details: ExecutionDetailDraft[],
  sets: string,
  fallbackRepetitions = DEFAULT_REPETITIONS_VALUE,
): ExecutionDetailDraft[] => {
  const setCount = resolvePyramidSetCount(sets);
  const sourceDetails = normalizeDropFamilyDetails(details, fallbackRepetitions);

  return Array.from({ length: setCount }, (_, index) => {
    const existingDetail = sourceDetails[index];
    const seedDetail = existingDetail ?? createDropFamilyExecutionDetail(
      sourceDetails[sourceDetails.length - 1],
      index,
      fallbackRepetitions,
    );

    return applyPyramidLoadToDetail({
      ...seedDetail,
      id: existingDetail?.id?.trim() || createExecutionDetailId(),
      block: existingDetail?.block || String(index + 1),
    });
  });
};

const normalizeExecutionMethodConfig = (
  methodConfig: ExecutionMethodConfigDraft | TrainingExerciseMethodConfig | null | undefined,
  method: string,
): ExecutionMethodConfigDraft | null => {
  if (isClusterSetTrainingExecutionMethod(method)) {
    return {
      roundRepetitions: normalizePositiveIntegerInput(String(methodConfig?.roundRepetitions ?? ''), 3),
      intraSetRest: normalizePositiveIntegerInput(String(methodConfig?.intraSetRest ?? ''), 4),
      fixedRestMin: null,
      fixedRestMax: null,
    };
  }

  if (isGvtTrainingExecutionMethod(method)) {
    return {
      fixedRestMin: 30,
      fixedRestMax: 60,
    };
  }

  return null;
};

const normalizeExecutionMethodConfigForPersist = (
  methodConfig: ExecutionMethodConfigDraft | null | undefined,
  method: string,
): TrainingExerciseMethodConfig | null => {
  if (isClusterSetTrainingExecutionMethod(method)) {
    return {
      roundRepetitions: normalizePositiveIntegerNumber(methodConfig?.roundRepetitions ?? '', 3),
      intraSetRest: normalizePositiveIntegerNumber(methodConfig?.intraSetRest ?? '', 4),
      fixedRestMin: null,
      fixedRestMax: null,
    };
  }

  if (isGvtTrainingExecutionMethod(method)) {
    return {
      fixedRestMin: 30,
      fixedRestMax: 60,
    };
  }

  return null;
};

const formatSecondsDraft = (value?: number | null, fallback?: number, allowZero = false) => {
  if (typeof value === 'number' && Number.isFinite(value) && (allowZero ? value >= 0 : value > 0)) {
    return String(Math.trunc(value));
  }

  if (typeof fallback === 'number') {
    return String(fallback);
  }

  return '';
};

const buildSpeedDraft = (execution: Pick<TrainingExerciseExecution, 'executionSpeed' | 'isometricSeconds' | 'concentricSeconds' | 'pauseSeconds' | 'eccentricSeconds'>): Pick<ExecutionDraft, 'executionSpeed' | 'isometricSeconds' | 'concentricSeconds' | 'pauseSeconds' | 'eccentricSeconds'> => {
  const executionSpeed = normalizeExecutionSpeed(execution.executionSpeed);

  if (executionSpeed === 'isometry') {
    return {
      executionSpeed,
      isometricSeconds: formatSecondsDraft(execution.isometricSeconds),
      concentricSeconds: '',
      pauseSeconds: '',
      eccentricSeconds: '',
    };
  }

  if (executionSpeed === 'cadence') {
    return {
      executionSpeed,
      isometricSeconds: '',
      concentricSeconds: formatSecondsDraft(execution.concentricSeconds, DEFAULT_CONCENTRIC_SECONDS, true),
      pauseSeconds: formatSecondsDraft(execution.pauseSeconds, DEFAULT_PAUSE_SECONDS, true),
      eccentricSeconds: formatSecondsDraft(execution.eccentricSeconds, DEFAULT_ECCENTRIC_SECONDS, true),
    };
  }

  return {
    executionSpeed: null,
    isometricSeconds: '',
    concentricSeconds: '',
    pauseSeconds: '',
    eccentricSeconds: '',
  };
};

const persistSpeedFields = (execution: ExecutionDraft) => buildExecutionSpeedFields({
  executionSpeed: execution.executionSpeed,
  isometricSeconds: normalizePositiveIntegerNumber(execution.isometricSeconds, 4),
  concentricSeconds: normalizeNonNegativeIntegerNumber(execution.concentricSeconds, 4),
  pauseSeconds: normalizeNonNegativeIntegerNumber(execution.pauseSeconds, 4),
  eccentricSeconds: normalizeNonNegativeIntegerNumber(execution.eccentricSeconds, 4),
});

const buildExecutionForMethodChange = (
  method: string,
  currentExecution: ExecutionDraft,
): ExecutionDraft => {
  const canonicalMethod = getCanonicalTrainingExecutionMethodValue(method);
  const methodDefinition = getTrainingExecutionMethodDefinition(canonicalMethod);
  const fallbackSets = normalizePositiveIntegerInput(currentExecution.sets, 2) || DEFAULT_NUMERIC_VALUE;
  const fallbackRepetitions = normalizePositiveIntegerInput(currentExecution.repetitions, 3)
    || buildRepetitionsSummaryFromDetails(currentExecution.details)
    || DEFAULT_REPETITIONS_VALUE;
  const fallbackRest = normalizePositiveIntegerInput(currentExecution.rest, 4)
    || buildRestSummaryFromDetails(currentExecution.details)
    || DEFAULT_REST_VALUE;
  const nextSets = methodDefinition.fixedSets != null ? String(methodDefinition.fixedSets) : fallbackSets;
  const nextRepetitions = methodDefinition.fixedRepetitions != null ? String(methodDefinition.fixedRepetitions) : fallbackRepetitions;
  const nextRest = methodDefinition.fixedRest != null ? String(methodDefinition.fixedRest) : fallbackRest;
  const nextDetails = isPyramidTrainingExecutionMethod(canonicalMethod)
    ? syncPyramidDetails(currentExecution.details, nextSets, nextRepetitions)
    : (isDropFamilyTrainingExecutionMethod(canonicalMethod)
      ? normalizeDropFamilyDetails(
        currentExecution.details,
        nextRepetitions,
        !isDropFamilyTrainingExecutionMethod(currentExecution.method),
      )
      : []);

  if (isTabataTrainingExecutionMethod(canonicalMethod)) {
    return {
      ...currentExecution,
      sets: nextSets,
      repetitions: '',
      rest: normalizePositiveIntegerInput(currentExecution.rest, 4) || nextRest || DEFAULT_TABATA_REST_VALUE,
      method: canonicalMethod,
      details: [],
      methodConfig: null,
      executionSeconds: normalizePositiveIntegerNumber(currentExecution.executionSeconds, 4) ?? DEFAULT_TABATA_EXECUTION_SECONDS,
    };
  }

  return {
    ...currentExecution,
    sets: nextSets,
    repetitions: isDropFamilyTrainingExecutionMethod(canonicalMethod)
      ? buildRepetitionsSummaryFromDetails(nextDetails) || nextRepetitions
      : nextRepetitions,
    rest: nextRest,
    method: canonicalMethod,
    details: nextDetails,
    methodConfig: normalizeExecutionMethodConfig(null, canonicalMethod),
  };
};

const normalizeExecutionForEditor = (execution: TrainingExerciseExecution): ExecutionDraft => {
  const draftExecution: ExecutionDraft = {
    sets: formatOptionalInteger(execution.sets),
    repetitions: formatOptionalInteger(execution.repetitions),
    rest: formatOptionalInteger(execution.rest),
    method: execution.method,
    persistedSetIds: execution.persistedSetIds,
    repetitionScope: normalizeRepetitionScope(execution.repetitionScope),
    details: execution.details.map((detail, index) => ({
      id: detail.id?.trim() || createExecutionDetailId(),
      repetitionsMin: formatOptionalInteger(detail.repetitionsMin),
      repetitionsMax: formatOptionalInteger(detail.repetitionsMax),
      loadPercentage: detail.loadPercentage != null ? String(Math.round(detail.loadPercentage)) : '',
      rest: formatOptionalInteger(detail.rest),
      block: formatOptionalInteger(detail.block) || String(index + 1),
    })),
    methodConfig: execution.methodConfig
      ? {
        roundRepetitions: formatOptionalInteger(execution.methodConfig.roundRepetitions),
        intraSetRest: formatOptionalInteger(execution.methodConfig.intraSetRest),
        fixedRestMin: execution.methodConfig.fixedRestMin ?? null,
        fixedRestMax: execution.methodConfig.fixedRestMax ?? null,
      }
      : null,
    ...buildSpeedDraft(execution),
    executionSeconds: execution.executionSeconds ?? null,
    exerciseType: execution.exerciseType ?? null,
  };
  const normalizedMethod = getCanonicalTrainingExecutionMethodValue(draftExecution.method);
  const methodDefinition = getTrainingExecutionMethodDefinition(normalizedMethod);
  const restPauseRepetitionsFromDetails = isRestPauseTrainingExecutionMethod(normalizedMethod)
    ? buildRangeValue(
      draftExecution.details[0]?.repetitionsMin ?? '',
      draftExecution.details[0]?.repetitionsMax ?? '',
      Boolean(draftExecution.details[0]?.repetitionsMin || draftExecution.details[0]?.repetitionsMax),
    )
    : '';
  const normalizedSets = methodDefinition.fixedSets != null
    ? String(methodDefinition.fixedSets)
    : (normalizePositiveIntegerInput(draftExecution.sets, 2) || (draftExecution.details.length > 0 ? String(draftExecution.details.length) : DEFAULT_NUMERIC_VALUE));
  const normalizedExecutionSeconds = normalizePositiveIntegerNumber(draftExecution.executionSeconds, 4);

  if (isTabataTrainingExecutionMethod(normalizedMethod)) {
    return {
      ...draftExecution,
      sets: normalizedSets,
      repetitions: '',
      rest: normalizePositiveIntegerInput(draftExecution.rest, 4) || DEFAULT_TABATA_REST_VALUE,
      method: normalizedMethod,
      details: [],
      methodConfig: null,
      executionSeconds: normalizedExecutionSeconds ?? DEFAULT_TABATA_EXECUTION_SECONDS,
    };
  }

  const normalizedRepetitions = methodDefinition.fixedRepetitions != null
    ? String(methodDefinition.fixedRepetitions)
    : (isRestPauseTrainingExecutionMethod(normalizedMethod)
      ? (restPauseRepetitionsFromDetails || buildRangeValue(draftExecution.repetitions, '', false) || DEFAULT_REPETITIONS_VALUE)
      : (normalizePositiveIntegerInput(draftExecution.repetitions, 3) || buildRepetitionsSummaryFromDetails(draftExecution.details) || DEFAULT_REPETITIONS_VALUE));
  const normalizedRest = methodDefinition.fixedRest != null
    ? String(methodDefinition.fixedRest)
    : (normalizePositiveIntegerInput(draftExecution.rest, 4) || buildRestSummaryFromDetails(draftExecution.details) || DEFAULT_REST_VALUE);
  const normalizedDetails = isPyramidTrainingExecutionMethod(normalizedMethod)
    ? syncPyramidDetails(draftExecution.details, normalizedSets, normalizedRepetitions)
    : (isDropFamilyTrainingExecutionMethod(normalizedMethod)
      ? normalizeDropFamilyDetails(draftExecution.details, normalizedRepetitions)
      : []);

  return {
    ...draftExecution,
    sets: normalizedSets,
    repetitions: normalizedRepetitions,
    rest: normalizedRest,
    method: normalizedMethod,
    details: normalizedDetails,
    methodConfig: normalizeExecutionMethodConfig(execution.methodConfig, normalizedMethod),
    executionSeconds: normalizedExecutionSeconds,
  };
};

const getContrastTextColor = (hexColor: string) => {
  const normalizedHex = hexColor.replace('#', '');

  if (normalizedHex.length !== 6) {
    return '#0f172a';
  }

  const red = Number.parseInt(normalizedHex.slice(0, 2), 16);
  const green = Number.parseInt(normalizedHex.slice(2, 4), 16);
  const blue = Number.parseInt(normalizedHex.slice(4, 6), 16);
  const luminance = ((red * 299) + (green * 587) + (blue * 114)) / 1000;

  return luminance >= 160 ? '#0f172a' : '#ffffff';
};

const validateExecution = (execution: ExecutionDraft): Partial<Record<NumericExecutionField, string>> => {
  const nextErrors: Partial<Record<NumericExecutionField, string>> = {};
  const usesTabataFields = isTabataTrainingExecutionMethod(execution.method);
  const usesDropFamilyDetails = isDropFamilyTrainingExecutionMethod(execution.method);
  const usesRestPauseFields = isRestPauseTrainingExecutionMethod(execution.method);
  const usesClusterFields = isClusterSetTrainingExecutionMethod(execution.method);
  const usesGvtFields = isGvtTrainingExecutionMethod(execution.method);

  if (!isPositiveIntegerValue(execution.sets)) {
    nextErrors.sets = 'Informe um número maior que 0 para séries.';
  }

  if (isIsometryExecutionSpeed(execution.executionSpeed) && !isPositiveIntegerValue(execution.isometricSeconds ?? '')) {
    nextErrors.isometricSeconds = 'Informe o tempo da execução.';
  }

  if (isCadenceExecutionSpeed(execution.executionSpeed)) {
    if (!isNonNegativeIntegerValue(execution.concentricSeconds ?? '')) {
      nextErrors.concentricSeconds = 'Informe o tempo da fase concêntrica.';
    }

    if (!isNonNegativeIntegerValue(execution.pauseSeconds ?? '')) {
      nextErrors.pauseSeconds = 'Informe o tempo da pausa.';
    }

    if (!isNonNegativeIntegerValue(execution.eccentricSeconds ?? '')) {
      nextErrors.eccentricSeconds = 'Informe o tempo da fase excêntrica.';
    }
  }

  if (usesTabataFields) {
    if (!execution.executionSeconds || execution.executionSeconds <= 0) {
      nextErrors.executionSeconds = 'Informe o tempo de execução em segundos.';
    }

    if (!isPositiveIntegerValue(execution.rest)) {
      nextErrors.rest = 'Informe o descanso em segundos.';
    }

    return nextErrors;
  }

  if (usesDropFamilyDetails) {
    const hasInvalidDetail = execution.details.some((detail) => {
      if (!isPositiveIntegerValue(detail.repetitionsMax || detail.repetitionsMin)) {
        return true;
      }

      if (!isPositiveIntegerValue(detail.block)) {
        return true;
      }

      if (isPyramidTrainingExecutionMethod(execution.method)) {
        if (getPyramidLoadPercentageValue(detail.repetitionsMax || detail.repetitionsMin) == null) {
          return true;
        }
      } else if (!isValidLoadPercentageValue(detail.loadPercentage)) {
        return true;
      }

      return false;
    });

    if (execution.details.length === 0 || hasInvalidDetail) {
      nextErrors.details = 'Revise repetições, carga e bloco nas linhas de execução.';
    }

    if (usesManualLoadPercentage(execution.method) && !isPositiveIntegerValue(execution.rest)) {
      nextErrors.rest = 'Informe um descanso maior que 0.';
    }

    return nextErrors;
  }

  if (usesClusterFields) {
    if (!isPositiveIntegerValue(execution.repetitions)) {
      nextErrors.repetitions = 'Informe o total de repetições.';
    }

    if (!isPositiveIntegerValue(execution.rest)) {
      nextErrors.rest = 'Informe um descanso maior que 0.';
    }

    if (!isPositiveIntegerValue(execution.methodConfig?.roundRepetitions ?? '')) {
      nextErrors.roundRepetitions = 'Informe as repetições por round.';
    }

    if (!isPositiveIntegerValue(execution.methodConfig?.intraSetRest ?? '')) {
      nextErrors.intraSetRest = 'Informe o descanso intra série.';
    }

    if (
      isPositiveIntegerValue(execution.repetitions)
      && isPositiveIntegerValue(execution.methodConfig?.roundRepetitions ?? '')
      && Number.parseInt(execution.methodConfig?.roundRepetitions ?? '', 10) > Number.parseInt(execution.repetitions, 10)
    ) {
      nextErrors.roundRepetitions = 'As repetições por round não podem ser maiores que as repetições totais.';
    }

    return nextErrors;
  }

  if (usesRestPauseFields) {
    if (!isPositiveIntegerValue(execution.rest)) {
      nextErrors.rest = 'Informe o descanso intra série.';
    }

    return nextErrors;
  }

  if (!isIsometryExecutionSpeed(execution.executionSpeed) && !isValidRepetitionValue(execution.repetitions, false)) {
    nextErrors.repetitions = 'Informe uma repetição válida.';
  }

  if (!usesGvtFields && !isPositiveIntegerValue(execution.rest)) {
    nextErrors.rest = 'Informe um descanso maior que 0.';
  }

  return nextErrors;
};

const getRestPauseInputs = (repetitionsValue: string) => {
  const parsedRange = parseRepetitionRangeValue(repetitionsValue);

  if (!parsedRange) {
    return { min: '', max: '' };
  }

  return repetitionsValue.includes('-')
    ? { min: parsedRange.min, max: parsedRange.max }
    : { min: parsedRange.min, max: '' };
};

const convertDetailDraftToExecution = (detail: ExecutionDetailDraft, index: number): TrainingExerciseExecutionDetail => ({
  id: detail.id?.trim() || createExecutionDetailId(),
  repetitionsMin: normalizePositiveIntegerNumber(detail.repetitionsMin, 3),
  repetitionsMax: normalizePositiveIntegerNumber(detail.repetitionsMax, 3),
  loadPercentage: normalizeLoadPercentageNumber(detail.loadPercentage),
  rest: normalizePositiveIntegerNumber(detail.rest, 4),
  block: normalizePositiveIntegerNumber(detail.block, 3) ?? (index + 1),
});

const ExerciseExecutionEditor: React.FC<ExerciseExecutionEditorProps> = ({
  execution,
  exerciseName,
  catalogExerciseId,
  structureType,
  anchorRect,
  methodOptions,
  isReadOnly = false,
  isTabataController = false,
  tabataControllerName = null,
  selectedExercises,
  onConfirm,
  onClose,
  onReplaceExercise,
}) => {
  const [localExecution, setLocalExecution] = useState<ExecutionDraft>(() => normalizeExecutionForEditor(execution));
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<NumericExecutionField, string>>>({});
  const [videoTarget, setVideoTarget] = useState<SelectedExercisePreview | null>(null);
  const [swapTarget, setSwapTarget] = useState<SelectedExercisePreview | null>(null);
  useEscapeToClose(onClose);
  const headerExercises = selectedExercises && selectedExercises.length > 0
    ? selectedExercises
    : [{ id: 'active', blockId: '', catalogExerciseId, name: exerciseName }];
  const isBulkSelection = headerExercises.length > 1;
  const activeHeaderExercise = headerExercises[0] ?? null;
  const initialScrollRef = useRef({ x: window.scrollX, y: window.scrollY });
  const [scrollSnapshot, setScrollSnapshot] = useState({ x: window.scrollX, y: window.scrollY });
  const [popoverHeight, setPopoverHeight] = useState(0);
  const popoverRef = useRef<HTMLDivElement>(null);
  const attentionAnimationRef = useRef<Animation | null>(null);

  const position = useMemo(() => {
    const scrollDeltaX = scrollSnapshot.x - initialScrollRef.current.x;
    const scrollDeltaY = scrollSnapshot.y - initialScrollRef.current.y;

    const shiftedAnchorRect = new DOMRect(
      anchorRect.x - scrollDeltaX,
      anchorRect.y - scrollDeltaY,
      anchorRect.width,
      anchorRect.height,
    );

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    let left = shiftedAnchorRect.left;
    const preferredTop = shiftedAnchorRect.bottom + POPOVER_GAP;
    const maximumTop = Math.max(VIEWPORT_MARGIN, viewportHeight - popoverHeight - VIEWPORT_MARGIN);
    const top = Math.max(VIEWPORT_MARGIN, Math.min(preferredTop, maximumTop));

    if (left + POPOVER_WIDTH > viewportWidth - VIEWPORT_MARGIN) {
      left = viewportWidth - POPOVER_WIDTH - VIEWPORT_MARGIN;
    }

    if (left < VIEWPORT_MARGIN) {
      left = VIEWPORT_MARGIN;
    }

    const arrowLeft = Math.max(
      12,
      Math.min(POPOVER_WIDTH - 24, shiftedAnchorRect.left + shiftedAnchorRect.width / 2 - left - ARROW_HEIGHT),
    );

    return { top, left, arrowLeft };
  }, [anchorRect, popoverHeight, scrollSnapshot.x, scrollSnapshot.y]);

  useEffect(() => {
    const updatePopoverHeight = () => {
      setPopoverHeight(popoverRef.current?.offsetHeight ?? 0);
    };

    updatePopoverHeight();
  }, [headerExercises.length, localExecution, fieldErrors, methodOptions.length]);

  useEffect(() => {
    const handleViewportChange = () => {
      setScrollSnapshot({ x: window.scrollX, y: window.scrollY });
    };

    window.addEventListener('scroll', handleViewportChange, true);
    window.addEventListener('resize', handleViewportChange);

    return () => {
      window.removeEventListener('scroll', handleViewportChange, true);
      window.removeEventListener('resize', handleViewportChange);
    };
  }, []);

  useEffect(() => {
    setLocalExecution(normalizeExecutionForEditor(execution));
    setFieldErrors({});
  }, [execution]);

  useEffect(() => () => {
    attentionAnimationRef.current?.cancel();
  }, []);

  const resolvedMethodOptions = useMemo(() => {
    const preferredByCanonical = new Map<string, ExecutionMethodOption>();
    const canUseTabata = isTabataSupportedTrainingGroupingType(structureType);

    methodOptions.forEach((option) => {
      const canonicalValue = getCanonicalTrainingExecutionMethodValue(option.value);

      if (!canonicalValue) {
        return;
      }

      const existing = preferredByCanonical.get(canonicalValue);
      const optionMatchesCanonical = option.value === canonicalValue;
      const existingMatchesCanonical = existing?.value === canonicalValue;

      if (!existing || (optionMatchesCanonical && !existingMatchesCanonical)) {
        preferredByCanonical.set(canonicalValue, option);
      }
    });

    return Array.from(preferredByCanonical.entries()).map(([canonicalValue, option]) => {
      const isTabataMethod = canonicalValue === 'tabata';
      const tabataDisabledForStructure = isTabataMethod && !canUseTabata;

      return {
        ...option,
        value: canonicalValue,
        label: getTrainingExecutionMethodDisplayLabel(canonicalValue, option.label) || option.label,
        disabled: tabataDisabledForStructure,
      };
    });
  }, [methodOptions, structureType]);

  const methodColorByValue = useMemo(
    () => resolvedMethodOptions.reduce<Record<string, string>>((accumulator, option) => {
      if (option.color) {
        accumulator[option.value] = option.color;
      }

      return accumulator;
    }, {}),
    [resolvedMethodOptions],
  );
  const usesIsometrySpeed = isIsometryExecutionSpeed(localExecution.executionSpeed);
  const usesCadenceSpeed = isCadenceExecutionSpeed(localExecution.executionSpeed);

  const renderMethodOption = useCallback((option: ExecutionMethodOption) => {
    const color = methodColorByValue[option.value] ?? null;

    return (
      <span className={styles.methodOptionContent}>
        {color ? <span className={styles.methodColorDot} style={{ backgroundColor: color }} aria-hidden="true" /> : null}
        <span className={styles.methodOptionLabel}>{option.label}</span>
      </span>
    );
  }, [methodColorByValue]);

  const structureLabel = TRAINING_EXERCISE_GROUPING_LABELS[structureType] ?? TRAINING_EXERCISE_GROUPING_LABELS.multiple_set;
  const structureColor = TRAINING_EXERCISE_GROUPING_COLORS[structureType] ?? null;
  const usesStructureField = usesStructureFieldForTrainingExecutionMethod(localExecution.method);
  const usesTabataFields = isTabataTrainingExecutionMethod(localExecution.method);
  const usesDropFamilyDetails = isDropFamilyTrainingExecutionMethod(localExecution.method);
  const usesPyramidDetails = isPyramidTrainingExecutionMethod(localExecution.method);
  const usesDropSetRestField = usesManualLoadPercentage(localExecution.method);
  const usesRestPauseFields = isRestPauseTrainingExecutionMethod(localExecution.method);
  const usesClusterFields = isClusterSetTrainingExecutionMethod(localExecution.method);
  const usesGvtFields = isGvtTrainingExecutionMethod(localExecution.method);
  const usesDefaultFields = !usesTabataFields && !usesDropFamilyDetails && !usesRestPauseFields && !usesClusterFields && !usesGvtFields;
  const methodDefinition = getTrainingExecutionMethodDefinition(localExecution.method);
  const dropDetailGridTemplateColumns = usesPyramidDetails
    ? 'repeat(3, minmax(0, 1fr))'
    : 'repeat(3, minmax(0, 1fr)) 32px';
  const structureTextColor = structureColor ? getContrastTextColor(structureColor) : null;
  const structureFieldStyle = structureColor
    ? {
      backgroundColor: structureColor,
      borderColor: structureColor,
      color: structureTextColor ?? '#0f172a',
      WebkitTextFillColor: structureTextColor ?? '#0f172a',
    }
    : undefined;
  const restPauseInputs = useMemo(() => getRestPauseInputs(localExecution.repetitions), [localExecution.repetitions]);
  const tabataHelperText = isReadOnly && tabataControllerName
    ? `Este exercício é sincronizado pelo primeiro card do Tabata: ${tabataControllerName}.`
    : (usesTabataFields && isTabataController && structureType !== 'multiple_set'
      ? 'As alterações feitas aqui serão aplicadas automaticamente aos demais exercícios do Tabata.'
      : '');

  const triggerPopoverAttention = useCallback(() => {
    const popoverElement = popoverRef.current;

    if (!popoverElement) {
      return;
    }

    attentionAnimationRef.current?.cancel();
    attentionAnimationRef.current = popoverElement.animate(
      [
        { transform: 'translate3d(0, 0, 0)', filter: 'brightness(1)' },
        { transform: 'translate3d(-4px, 0, 0)', filter: 'brightness(1.03)' },
        { transform: 'translate3d(4px, 0, 0)', filter: 'brightness(1.02)' },
        { transform: 'translate3d(-2px, 0, 0)', filter: 'brightness(1.01)' },
        { transform: 'translate3d(0, 0, 0)', filter: 'brightness(1)' },
      ],
      {
        duration: 240,
        easing: 'ease-out',
      },
    );
  }, []);

  useEffect(() => {
    const handlePointerDownOutside = (event: MouseEvent) => {
      const popoverElement = popoverRef.current;
      const target = event.target;

      if (!popoverElement || !(target instanceof Node)) {
        return;
      }

      if (popoverElement.contains(target)) {
        return;
      }

      if (videoTarget || swapTarget) {
        return;
      }

      if (target instanceof Element && target.closest('[role="dialog"]')) {
        return;
      }

      triggerPopoverAttention();
    };

    document.addEventListener('mousedown', handlePointerDownOutside, true);

    return () => {
      document.removeEventListener('mousedown', handlePointerDownOutside, true);
    };
  }, [swapTarget, triggerPopoverAttention, videoTarget]);

  const handleMethodChange = useCallback((value: string) => {
    setLocalExecution((current) => buildExecutionForMethodChange(value, current));
    setFieldErrors({});
  }, []);

  const handleExecutionSpeedChange = useCallback((value: string) => {
    const nextSpeed = normalizeExecutionSpeed(value);

    setLocalExecution((current) => {
      if (nextSpeed === 'isometry') {
        return {
          ...current,
          executionSpeed: nextSpeed,
          repetitions: '',
          isometricSeconds: current.isometricSeconds || '',
          concentricSeconds: '',
          pauseSeconds: '',
          eccentricSeconds: '',
        };
      }

      if (nextSpeed === 'cadence') {
        return {
          ...current,
          executionSpeed: nextSpeed,
          repetitions: current.repetitions || DEFAULT_REPETITIONS_VALUE,
          isometricSeconds: '',
          concentricSeconds: isNonNegativeIntegerValue(current.concentricSeconds ?? '')
            ? current.concentricSeconds
            : String(DEFAULT_CONCENTRIC_SECONDS),
          pauseSeconds: isNonNegativeIntegerValue(current.pauseSeconds ?? '')
            ? current.pauseSeconds
            : String(DEFAULT_PAUSE_SECONDS),
          eccentricSeconds: isNonNegativeIntegerValue(current.eccentricSeconds ?? '')
            ? current.eccentricSeconds
            : String(DEFAULT_ECCENTRIC_SECONDS),
        };
      }

      return {
        ...current,
        executionSpeed: null,
        isometricSeconds: '',
        concentricSeconds: '',
        pauseSeconds: '',
        eccentricSeconds: '',
      };
    });
    setFieldErrors((currentErrors) => ({
      ...currentErrors,
      isometricSeconds: '',
      concentricSeconds: '',
      pauseSeconds: '',
      eccentricSeconds: '',
    }));
  }, []);

  const handleSecondsFieldChange = useCallback((field: 'isometricSeconds' | 'concentricSeconds' | 'pauseSeconds' | 'eccentricSeconds' | 'rest', value: string) => {
    setLocalExecution((current) => ({
      ...current,
      [field]: value,
    }));
    setFieldErrors((currentErrors) => ({
      ...currentErrors,
      [field]: '',
    }));
  }, []);

  const handleSetsChange = useCallback((value: string) => {
    const normalizedValue = normalizePositiveIntegerInput(value, 2);

    setLocalExecution((current) => {
      if (isGvtTrainingExecutionMethod(current.method)) {
        return current;
      }

      if (isPyramidTrainingExecutionMethod(current.method)) {
        const nextDetails = syncPyramidDetails(current.details, normalizedValue, current.repetitions);

        return {
          ...current,
          sets: normalizedValue,
          details: nextDetails,
          repetitions: buildRepetitionsSummaryFromDetails(nextDetails) || current.repetitions,
        };
      }

      return {
        ...current,
        sets: normalizedValue,
      };
    });
    setFieldErrors((currentErrors) => ({
      ...currentErrors,
      sets: '',
    }));
  }, []);

  const handleRepetitionsChange = useCallback((value: string) => {
    const normalizedValue = normalizePositiveIntegerInput(value, 3);

    setLocalExecution((current) => ({
      ...current,
      repetitions: normalizedValue,
    }));
    setFieldErrors((currentErrors) => ({
      ...currentErrors,
      repetitions: '',
    }));
  }, []);

  const handleTotalClusterRepetitionsChange = useCallback((value: string) => {
    const normalizedValue = normalizePositiveIntegerInput(value, 3);

    setLocalExecution((current) => ({
      ...current,
      repetitions: normalizedValue,
    }));
    setFieldErrors((currentErrors) => ({
      ...currentErrors,
      repetitions: '',
    }));
  }, []);

  const handleRestPauseRepetitionsChange = useCallback((field: 'min' | 'max', value: string) => {
    const normalizedValue = normalizePositiveIntegerInput(value, 3);

    setLocalExecution((current) => {
      const currentInputs = getRestPauseInputs(current.repetitions);
      const nextMin = field === 'min' ? normalizedValue : currentInputs.min;
      const nextMax = field === 'max' ? normalizedValue : currentInputs.max;

      return {
        ...current,
        repetitions: buildRangeValue(nextMin, nextMax, true),
      };
    });
    setFieldErrors((currentErrors) => ({
      ...currentErrors,
      repetitions: '',
    }));
  }, []);

  const handleClusterFieldChange = useCallback((field: 'roundRepetitions' | 'intraSetRest', value: string) => {
    const normalizedValue = normalizePositiveIntegerInput(value, field === 'intraSetRest' ? 4 : 3);

    setLocalExecution((current) => ({
      ...current,
      methodConfig: {
        ...(current.methodConfig ?? {}),
        [field]: normalizedValue,
      },
    }));
    setFieldErrors((currentErrors) => ({
      ...currentErrors,
      [field]: '',
    }));
  }, []);

  const handleRestChange = useCallback((value: string) => {
    setLocalExecution((current) => ({
      ...current,
      rest: value,
    }));
    setFieldErrors((currentErrors) => ({
      ...currentErrors,
      rest: '',
    }));
  }, []);

  const handleRepetitionScopeChange = useCallback((value: string) => {
    setLocalExecution((current) => ({
      ...current,
      repetitionScope: normalizeRepetitionScope(value),
    }));
  }, []);

  const handleTabataRestChange = useCallback((value: string) => {
    const normalizedValue = normalizePositiveIntegerInput(value, 4);

    setLocalExecution((current) => ({
      ...current,
      rest: normalizedValue,
    }));
    setFieldErrors((currentErrors) => ({
      ...currentErrors,
      rest: '',
    }));
  }, []);

  const handleExecutionSecondsChange = useCallback((value: string) => {
    const normalizedValue = normalizePositiveIntegerNumber(value, 4);

    setLocalExecution((current) => ({
      ...current,
      executionSeconds: normalizedValue,
    }));
    setFieldErrors((currentErrors) => ({
      ...currentErrors,
      executionSeconds: '',
    }));
  }, []);

  const handleExecutionDetailChange = useCallback((detailIndex: number, field: ExecutionDetailField, value: string) => {
    const normalizedValue = field === 'loadPercentage'
      ? normalizeLoadPercentageInput(value)
      : normalizePositiveIntegerInput(value, 3);

    setLocalExecution((current) => {
      if (isPyramidTrainingExecutionMethod(current.method) && field === 'loadPercentage') {
        return current;
      }

      const mappedDetails = current.details.map((detail, index) => (
        index === detailIndex
          ? { ...detail, [field]: normalizedValue }
          : detail
      ));
      const nextDetails = isPyramidTrainingExecutionMethod(current.method)
        ? syncPyramidDetails(mappedDetails, current.sets, current.repetitions)
        : normalizeDropFamilyDetails(mappedDetails, current.repetitions);

      return {
        ...current,
        details: nextDetails,
        repetitions: buildRepetitionsSummaryFromDetails(nextDetails) || current.repetitions,
      };
    });
    setFieldErrors((currentErrors) => ({
      ...currentErrors,
      details: '',
    }));
  }, []);

  const handleAddExecutionDetail = useCallback(() => {
    setLocalExecution((current) => {
      if (!isDropFamilyTrainingExecutionMethod(current.method) || isPyramidTrainingExecutionMethod(current.method)) {
        return current;
      }

      const lastDetail = current.details[current.details.length - 1];
      const nextBlockValue = getNextSequentialBlockValue(current.details);
      const nextDetails = [
        ...normalizeDropFamilyDetails(current.details, current.repetitions),
        createDropFamilyExecutionDetail(
          {
            repetitionsMin: lastDetail?.repetitionsMin,
            repetitionsMax: lastDetail?.repetitionsMax,
            loadPercentage: lastDetail?.loadPercentage,
            rest: lastDetail?.rest,
            block: nextBlockValue,
          },
          current.details.length,
          current.repetitions,
        ),
      ];

      return {
        ...current,
        details: nextDetails,
        repetitions: buildRepetitionsSummaryFromDetails(nextDetails) || current.repetitions,
      };
    });
    setFieldErrors((currentErrors) => ({
      ...currentErrors,
      details: '',
    }));
  }, []);

  const handleRemoveExecutionDetail = useCallback((detailIndex: number) => {
    setLocalExecution((current) => {
      if (
        !isDropFamilyTrainingExecutionMethod(current.method)
        || isPyramidTrainingExecutionMethod(current.method)
        || current.details.length <= 1
      ) {
        return current;
      }

      const nextDetails = current.details.filter((_, index) => index !== detailIndex);

      return {
        ...current,
        details: nextDetails,
        repetitions: buildRepetitionsSummaryFromDetails(nextDetails) || current.repetitions,
      };
    });
    setFieldErrors((currentErrors) => ({
      ...currentErrors,
      details: '',
    }));
  }, []);

  const handlePositiveIntegerKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (['e', 'E', '+', '-', '.', ','].includes(event.key)) {
      event.preventDefault();
    }
  }, []);

  const handleConfirm = useCallback(() => {
    if (isReadOnly) {
      onClose();
      return;
    }

    const methodValue = getCanonicalTrainingExecutionMethodValue(localExecution.method);
    const currentMethodDefinition = getTrainingExecutionMethodDefinition(methodValue);
    const normalizedMethodConfig = normalizeExecutionMethodConfigForPersist(localExecution.methodConfig, methodValue);
    let normalizedExecution: TrainingExerciseExecution;

    if (isTabataTrainingExecutionMethod(methodValue)) {
      normalizedExecution = {
        ...(localExecution as unknown as TrainingExerciseExecution),
        sets: normalizePositiveIntegerNumber(localExecution.sets, 2),
        method: methodValue,
        repetitions: null,
        rest: normalizePositiveIntegerNumber(localExecution.rest, 4),
        details: [],
        methodConfig: null,
        executionSeconds: normalizePositiveIntegerNumber(localExecution.executionSeconds, 4),
      };
    } else if (isDropFamilyTrainingExecutionMethod(methodValue)) {
      const persistDropFamilyRest = usesManualLoadPercentage(methodValue);
      const normalizedRest = persistDropFamilyRest
        ? normalizePositiveIntegerNumber(localExecution.rest, 4)
        : null;
      const normalizedDetails = isPyramidTrainingExecutionMethod(methodValue)
        ? syncPyramidDetails(localExecution.details, localExecution.sets, localExecution.repetitions)
        : normalizeDropFamilyDetails(localExecution.details, localExecution.repetitions);

      normalizedExecution = {
        ...(localExecution as unknown as TrainingExerciseExecution),
        sets: normalizePositiveIntegerNumber(localExecution.sets, 2),
        method: methodValue,
        repetitions: normalizePositiveIntegerNumber(buildRepetitionsSummaryFromDetails(normalizedDetails), 3),
        rest: normalizedRest,
        details: normalizedDetails.map((detail, index) => {
          const persistedDetail = convertDetailDraftToExecution(detail, index);
          const detailWithRest = persistDropFamilyRest
            ? { ...persistedDetail, rest: normalizedRest }
            : persistedDetail;

          if (!isPyramidTrainingExecutionMethod(methodValue)) {
            return detailWithRest;
          }

          return {
            ...detailWithRest,
            loadPercentage: getPyramidLoadPercentageValue(detail.repetitionsMax || detail.repetitionsMin),
          };
        }),
        methodConfig: null,
      };
    } else if (isClusterSetTrainingExecutionMethod(methodValue)) {
      normalizedExecution = {
        ...(localExecution as unknown as TrainingExerciseExecution),
        sets: normalizePositiveIntegerNumber(localExecution.sets, 2),
        method: methodValue,
        repetitions: normalizePositiveIntegerNumber(localExecution.repetitions, 3),
        rest: normalizePositiveIntegerNumber(localExecution.rest, 4),
        details: [],
        methodConfig: normalizedMethodConfig,
      };
    } else if (isGvtTrainingExecutionMethod(methodValue)) {
      normalizedExecution = {
        ...(localExecution as unknown as TrainingExerciseExecution),
        method: methodValue,
        sets: currentMethodDefinition.fixedSets ?? 10,
        repetitions: currentMethodDefinition.fixedRepetitions ?? 10,
        rest: currentMethodDefinition.fixedRest ?? 45,
        details: [],
        methodConfig: normalizedMethodConfig,
      };
    } else if (usesRestPauseFields) {
      const parsedMinValue = normalizePositiveIntegerNumber(restPauseInputs.min, 3);
      const parsedMaxValue = normalizePositiveIntegerNumber(restPauseInputs.max, 3);
      const minValue = parsedMinValue ?? parsedMaxValue;
      const maxValue = parsedMaxValue ?? parsedMinValue;
      const normalizedSets = normalizePositiveIntegerNumber(localExecution.sets, 2) ?? 1;
      const normalizedRest = normalizePositiveIntegerNumber(localExecution.rest, 4);

      normalizedExecution = {
        ...(localExecution as unknown as TrainingExerciseExecution),
        sets: normalizedSets,
        method: methodValue,
        repetitions: maxValue,
        rest: normalizedRest,
        details: Array.from({ length: normalizedSets }, (_, index) => ({
          id: localExecution.details[index]?.id?.trim() || createExecutionDetailId(),
          repetitionsMin: minValue,
          repetitionsMax: maxValue,
          loadPercentage: getPyramidLoadPercentageValue(maxValue ?? minValue),
          rest: normalizedRest,
          block: index + 1,
        })),
        methodConfig: null,
      };
    } else {
      normalizedExecution = {
        ...(localExecution as unknown as TrainingExerciseExecution),
        sets: normalizePositiveIntegerNumber(localExecution.sets, 2),
        method: methodValue,
        repetitions: normalizePositiveIntegerNumber(localExecution.repetitions, 3),
        rest: normalizePositiveIntegerNumber(localExecution.rest, 4),
        details: [],
        methodConfig: null,
      };
    }

    const speedFields = persistSpeedFields(localExecution);
    normalizedExecution = {
      ...normalizedExecution,
      ...speedFields,
      repetitionScope: normalizeRepetitionScope(localExecution.repetitionScope),
      repetitions: speedFields.executionSpeed === 'isometry' ? null : normalizedExecution.repetitions,
      details: speedFields.executionSpeed === 'isometry'
        ? normalizedExecution.details.map((detail) => ({
          ...detail,
          repetitionsMin: null,
          repetitionsMax: null,
        }))
        : normalizedExecution.details,
    };

    const nextErrors = validateExecution(normalizeExecutionForEditor(normalizedExecution));

    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      return;
    }

    onConfirm(normalizedExecution);
  }, [isReadOnly, localExecution, onClose, onConfirm, restPauseInputs.max, restPauseInputs.min, usesRestPauseFields]);

  return ReactDOM.createPortal(
    <>
    <div
      ref={popoverRef}
      className={styles.popover}
      style={{ top: position.top, left: position.left }}
      role="dialog"
      aria-label="Configurar execução do exercício"
    >
      <div
        className={styles.arrow}
        style={{ left: position.arrowLeft }}
      />

      <div className={`${styles.header}${isBulkSelection ? ` ${styles.headerBulk}` : ''}`}>
        <div className={`${styles.headerIdentity}${isBulkSelection ? ` ${styles.headerIdentityBulk}` : ''}`}>
          {isBulkSelection ? (
            <>
              <span className={styles.title}>Execução</span>
              <div className={styles.selectedExerciseList}>
                {headerExercises.map((exercise) => (
                  <div key={exercise.id} className={styles.selectedExerciseItem}>
                    <ExerciseThumbnailImage
                      exerciseId={exercise.catalogExerciseId}
                      exerciseName={exercise.name}
                      alt=""
                      className={styles.headerThumbnail}
                    />
                    <span className={styles.selectedExerciseName} title={exercise.name}>
                      {exercise.name}
                    </span>
                    <div className={styles.selectedExerciseActions}>
                      <button
                        type="button"
                        className={styles.headerIconButton}
                        onClick={() => setVideoTarget(exercise)}
                        aria-label={`Ver vídeo de ${exercise.name}`}
                        title="Ver vídeo"
                      >
                        <Icon name="play_circle" size={18} />
                      </button>
                      {!isReadOnly && onReplaceExercise ? (
                        <button
                          type="button"
                          className={styles.headerIconButton}
                          onClick={() => setSwapTarget(exercise)}
                          aria-label={`Trocar ${exercise.name}`}
                          title="Trocar exercício"
                        >
                          <Icon name="change_circle" size={18} />
                        </button>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <ExerciseThumbnailImage
                exerciseId={headerExercises[0]?.catalogExerciseId ?? catalogExerciseId}
                exerciseName={headerExercises[0]?.name ?? exerciseName}
                alt=""
                className={styles.headerThumbnail}
              />
              <div className={styles.headerText}>
                <span className={styles.title}>Execução</span>
                <span className={styles.subtitle} title={headerExercises[0]?.name ?? exerciseName}>
                  {headerExercises[0]?.name ?? exerciseName}
                </span>
              </div>
            </>
          )}
        </div>

        <div className={styles.headerActions}>
          {!isBulkSelection && activeHeaderExercise ? (
            <button
              type="button"
              className={styles.headerIconButton}
              onClick={() => setVideoTarget(activeHeaderExercise)}
              aria-label="Ver vídeo"
              title="Ver vídeo"
            >
              <Icon name="play_circle" size={18} />
            </button>
          ) : null}
          {!isBulkSelection && activeHeaderExercise && !isReadOnly && onReplaceExercise ? (
            <button
              type="button"
              className={styles.headerIconButton}
              onClick={() => setSwapTarget(activeHeaderExercise)}
              aria-label="Trocar exercício"
              title="Trocar exercício"
            >
              <Icon name="change_circle" size={18} />
            </button>
          ) : null}
          <button
            type="button"
            className={styles.headerIconButton}
            onClick={onClose}
            aria-label="Fechar editor de execução"
          >
            <Icon name="close" size={16} className={styles.closeButtonIcon} />
          </button>
        </div>
      </div>

      <div className={styles.body}>
        {tabataHelperText ? (
          <span className={styles.helperText}>{tabataHelperText}</span>
        ) : null}

        <div className={styles.structureSection}>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor="exercise-execution-structure">Estrutura do treino</label>
            <input
              id="exercise-execution-structure"
              className={`${styles.fieldInput} ${styles.fieldInputDisabled}`}
              type="text"
              value={structureLabel}
              placeholder={structureLabel}
              disabled
              readOnly
              style={structureFieldStyle}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>Método</label>
            <SearchableSelect
              className={`${styles.fieldSelect}${isReadOnly ? ` ${styles.fieldInputDisabled}` : ''}`}
              value={localExecution.method}
              onChange={handleMethodChange}
              options={resolvedMethodOptions}
              triggerFormat={renderMethodOption}
              optionFormat={renderMethodOption}
              placeholder="Selecionar método"
              noResultsText="Nenhum método encontrado"
              readOnly={isReadOnly}
            />
          </div>
        </div>

        {usesStructureField ? (
          <div className={styles.structureSection}>
            {usesTabataFields ? (
              <div className={`${styles.compactFieldsRow} ${styles.tabataFieldsRow}`}>
                <div className={`${styles.fieldGroup} ${styles.seriesField} ${styles.tabataFieldGroup}${isReadOnly ? ` ${styles.disabledFieldGroup}` : ''}`}>
                  <label className={styles.fieldLabel} htmlFor="exercise-execution-sets">Número de séries</label>
                  <NumericStepperInput
                    id="exercise-execution-sets"
                    value={localExecution.sets}
                    onChange={handleSetsChange}
                    onKeyDown={handlePositiveIntegerKeyDown}
                    min={1}
                    max={99}
                    placeholder="1"
                    ariaLabel="Número de séries"
                    hasError={Boolean(fieldErrors.sets)}
                    disabled={isReadOnly}
                  />
                </div>

                <div className={`${styles.fieldGroup} ${styles.tabataTimeField} ${styles.tabataFieldGroup}${isReadOnly ? ` ${styles.disabledFieldGroup}` : ''}`}>
                  <label className={styles.fieldLabel} htmlFor="exercise-execution-seconds">Tempo de exec. (s)</label>
                  <NumericStepperInput
                    id="exercise-execution-seconds"
                    value={localExecution.executionSeconds ? String(localExecution.executionSeconds) : ''}
                    onChange={handleExecutionSecondsChange}
                    onKeyDown={handlePositiveIntegerKeyDown}
                    min={1}
                    max={9999}
                    placeholder="20"
                    ariaLabel="Tempo de execução em segundos"
                    hasError={Boolean(fieldErrors.executionSeconds)}
                    disabled={isReadOnly}
                  />
                </div>

                <div className={`${styles.fieldGroup} ${styles.tabataTimeField} ${styles.tabataFieldGroup}${isReadOnly ? ` ${styles.disabledFieldGroup}` : ''}`}>
                  <label className={styles.fieldLabel} htmlFor="exercise-execution-tabata-rest">Descanso (s)</label>
                  <NumericStepperInput
                    id="exercise-execution-tabata-rest"
                    value={localExecution.rest}
                    onChange={handleTabataRestChange}
                    onKeyDown={handlePositiveIntegerKeyDown}
                    min={0}
                    max={9999}
                    placeholder="10"
                    ariaLabel="Descanso em segundos"
                    hasError={Boolean(fieldErrors.rest)}
                    disabled={isReadOnly}
                  />
                </div>
              </div>
            ) : null}

            {usesDefaultFields ? (
              <div className={styles.compactFieldsRow}>
                <div className={`${styles.fieldGroup} ${styles.seriesField}`}>
                  <label className={`${styles.fieldLabel} ${styles.noWrapLabel}`} htmlFor="exercise-execution-sets">Número de séries</label>
                  <NumericStepperInput
                    id="exercise-execution-sets"
                    value={localExecution.sets}
                    onChange={handleSetsChange}
                    onKeyDown={handlePositiveIntegerKeyDown}
                    min={1}
                    max={99}
                    placeholder="1"
                    ariaLabel="Número de séries"
                  />
                </div>

                {usesDefaultFields && !usesIsometrySpeed ? (
                <div className={`${styles.fieldGroup} ${styles.repetitionRangeField}`}>
                  <label className={`${styles.fieldLabel} ${styles.noWrapLabel}`} htmlFor="exercise-execution-repetitions">Repetição</label>
                  <NumericStepperInput
                    id="exercise-execution-repetitions"
                    value={localExecution.repetitions}
                    onChange={handleRepetitionsChange}
                    onKeyDown={handlePositiveIntegerKeyDown}
                    min={1}
                    max={999}
                    placeholder="12"
                    ariaLabel="Repetição"
                    hasError={Boolean(fieldErrors.repetitions)}
                  />
                </div>
                ) : null}

                {usesDefaultFields && !usesIsometrySpeed ? (
                <div className={`${styles.fieldGroup} ${styles.repetitionScopeField}`}>
                  <label className={`${styles.fieldLabel} ${styles.noWrapLabel}`} htmlFor="exercise-execution-repetition-scope">Tipo de repetição</label>
                  <SearchableSelect
                    className={`${styles.fieldSelect}${isReadOnly ? ` ${styles.fieldInputDisabled}` : ''}`}
                    value={localExecution.repetitionScope}
                    onChange={handleRepetitionScopeChange}
                    options={EXERCISE_REPETITION_SCOPE_OPTIONS}
                    placeholder="Total"
                    noResultsText="Nenhuma opção encontrada"
                    allowEmpty={false}
                    readOnly={isReadOnly}
                  />
                </div>
                ) : null}

                <DurationPickerField
                  id="exercise-execution-rest"
                  label="Descanso"
                  value={localExecution.rest}
                  onChange={handleRestChange}
                  error={fieldErrors.rest}
                  placeholder="Configurar intervalo"
                  containerClassName={`${styles.durationField} ${styles.smallDurationField}`}
                  maxMinutes={59}
                  defaultSeconds={DEFAULT_REST_SECONDS}
                />
              </div>
            ) : null}

            {usesDropFamilyDetails ? (
              <div className={styles.compactFieldsRow}>
                <div className={`${styles.fieldGroup} ${styles.seriesField}`}>
                  <label className={`${styles.fieldLabel} ${styles.noWrapLabel}`} htmlFor="exercise-execution-sets">Número de séries</label>
                  <NumericStepperInput
                    id="exercise-execution-sets"
                    value={localExecution.sets}
                    onChange={handleSetsChange}
                    onKeyDown={handlePositiveIntegerKeyDown}
                    min={1}
                    max={99}
                    placeholder="1"
                    ariaLabel="Número de séries"
                  />
                </div>

                {usesDropSetRestField ? (
                  <DurationPickerField
                    id="exercise-execution-drop-rest"
                    label="Descanso"
                    value={localExecution.rest}
                    onChange={handleRestChange}
                    error={fieldErrors.rest}
                    placeholder="Configurar intervalo"
                    containerClassName={`${styles.durationField} ${styles.smallDurationField}`}
                    maxMinutes={59}
                    defaultSeconds={DEFAULT_REST_SECONDS}
                  />
                ) : null}
              </div>
            ) : null}
          </div>
        ) : usesRestPauseFields ? (
          <div className={styles.compactFieldsRow}>
            <div className={`${styles.fieldGroup} ${styles.seriesField}`}>
              <label className={`${styles.fieldLabel} ${styles.noWrapLabel}`} htmlFor="exercise-execution-sets">Número de séries</label>
              <NumericStepperInput
                id="exercise-execution-sets"
                value={localExecution.sets}
                onChange={handleSetsChange}
                onKeyDown={handlePositiveIntegerKeyDown}
                min={1}
                max={99}
                placeholder="1"
                ariaLabel="Número de séries"
              />
            </div>

            <div className={`${styles.fieldGroup} ${styles.repetitionBoundsField}`}>
              <label className={`${styles.fieldLabel} ${styles.noWrapLabel}`} htmlFor="exercise-execution-repetitions-min">Repetições</label>
              <NumericStepperInput
                id="exercise-execution-repetitions-min"
                value={restPauseInputs.min}
                onChange={(value) => handleRestPauseRepetitionsChange('min', value)}
                onKeyDown={handlePositiveIntegerKeyDown}
                min={1}
                max={999}
                placeholder="8"
                ariaLabel="Repetições"
                hasError={Boolean(fieldErrors.repetitions)}
              />
            </div>

            <div className={`${styles.fieldGroup} ${styles.repetitionBoundsField}`}>
              <label className={styles.fieldLabel} htmlFor="exercise-execution-repetitions-max">RM (1ª execução)</label>
              <NumericStepperInput
                id="exercise-execution-repetitions-max"
                value={restPauseInputs.max}
                onChange={(value) => handleRestPauseRepetitionsChange('max', value)}
                onKeyDown={handlePositiveIntegerKeyDown}
                min={1}
                max={999}
                placeholder="12"
                ariaLabel="RM da primeira execução"
                hasError={Boolean(fieldErrors.repetitions)}
              />
            </div>
          </div>
        ) : usesClusterFields ? (
          <div className={styles.clusterFieldsRow}>
            <div className={`${styles.fieldGroup} ${styles.clusterNumericField}`}>
              <label className={`${styles.fieldLabel} ${styles.noWrapLabel}`} htmlFor="exercise-execution-sets">Número de séries</label>
              <NumericStepperInput
                id="exercise-execution-sets"
                value={localExecution.sets}
                onChange={handleSetsChange}
                onKeyDown={handlePositiveIntegerKeyDown}
                min={1}
                max={99}
                placeholder="1"
                ariaLabel="Número de séries"
              />
            </div>

            <div className={`${styles.fieldGroup} ${styles.clusterNumericField}`}>
              <label className={`${styles.fieldLabel} ${styles.noWrapLabel}`} htmlFor="exercise-execution-total-repetitions">Repetições totais</label>
              <NumericStepperInput
                id="exercise-execution-total-repetitions"
                value={localExecution.repetitions}
                onChange={handleTotalClusterRepetitionsChange}
                onKeyDown={handlePositiveIntegerKeyDown}
                min={1}
                max={999}
                placeholder="20"
                ariaLabel="Repetições totais"
                hasError={Boolean(fieldErrors.repetitions)}
              />
            </div>

            <div className={`${styles.fieldGroup} ${styles.clusterNumericField}`}>
              <label className={`${styles.fieldLabel} ${styles.noWrapLabel}`} htmlFor="exercise-execution-round-repetitions">Repetições por round</label>
              <NumericStepperInput
                id="exercise-execution-round-repetitions"
                value={localExecution.methodConfig?.roundRepetitions ?? ''}
                onChange={(value) => handleClusterFieldChange('roundRepetitions', value)}
                onKeyDown={handlePositiveIntegerKeyDown}
                min={1}
                max={999}
                placeholder="4"
                ariaLabel="Repetições por round"
                hasError={Boolean(fieldErrors.roundRepetitions)}
              />
            </div>
          </div>
        ) : usesGvtFields ? (
          <div className={styles.compactFieldsRow}>
            <div className={`${styles.fieldGroup} ${styles.seriesField}`}>
              <label className={`${styles.fieldLabel} ${styles.noWrapLabel}`} htmlFor="exercise-execution-sets">Número de séries</label>
              <NumericStepperInput
                id="exercise-execution-sets"
                value={methodDefinition.fixedSets ?? '10'}
                onChange={() => undefined}
                min={1}
                max={99}
                placeholder="10"
                ariaLabel="Número de séries"
                disabled
                readOnly
              />
            </div>

            <div className={`${styles.fieldGroup} ${styles.smallNumericField}`}>
              <label className={styles.fieldLabel}>Repetições</label>
              <NumericStepperInput
                value={methodDefinition.fixedRepetitions ?? '10'}
                onChange={() => undefined}
                min={1}
                max={999}
                placeholder="10"
                ariaLabel="Repetições"
                disabled
                readOnly
              />
            </div>

            <div className={`${styles.fieldGroup} ${styles.smallDurationField}`}>
              <label className={styles.fieldLabel}>Descanso</label>
              <input
                className={`${styles.fieldInput} ${styles.fieldInputDisabled}`}
                type="text"
                value={methodDefinition.fixedRestLabel ?? DEFAULT_GVT_REST_LABEL}
                placeholder={DEFAULT_GVT_REST_LABEL}
                disabled
                readOnly
              />
            </div>
          </div>
        ) : (
          <div className={styles.compactSingleFieldRow}>
            <div className={`${styles.fieldGroup} ${styles.seriesField}`}>
              <label className={`${styles.fieldLabel} ${styles.noWrapLabel}`} htmlFor="exercise-execution-sets">Número de séries</label>
              <NumericStepperInput
                id="exercise-execution-sets"
                value={localExecution.sets}
                onChange={handleSetsChange}
                onKeyDown={handlePositiveIntegerKeyDown}
                min={1}
                max={99}
                placeholder="1"
                ariaLabel="Número de séries"
                disabled={usesGvtFields}
                readOnly={usesGvtFields}
              />
            </div>
          </div>
        )}

        {fieldErrors.sets ? (
          <div className={styles.errorList}>
            <span className={styles.errorText}>{fieldErrors.sets}</span>
          </div>
        ) : null}

        {usesTabataFields && (fieldErrors.executionSeconds || fieldErrors.rest) ? (
          <div className={styles.errorList}>
            {fieldErrors.executionSeconds ? <span className={styles.errorText}>{fieldErrors.executionSeconds}</span> : null}
            {fieldErrors.rest ? <span className={styles.errorText}>{fieldErrors.rest}</span> : null}
          </div>
        ) : null}

        {usesDefaultFields ? (
          <>
            {fieldErrors.repetitions ? (
              <div className={styles.errorList}>
                <span className={styles.errorText}>{fieldErrors.repetitions}</span>
              </div>
            ) : null}
          </>
        ) : null}

        {usesRestPauseFields ? (
          <>
            {fieldErrors.repetitions ? (
              <div className={styles.errorList}>
                <span className={styles.errorText}>{fieldErrors.repetitions}</span>
              </div>
            ) : null}

            <DurationPickerField
              id="exercise-execution-rest"
              label="Descanso intra série"
              value={localExecution.rest}
              onChange={handleRestChange}
              error={fieldErrors.rest}
              placeholder="Configurar intervalo"
              containerClassName={styles.durationField}
              maxMinutes={59}
              defaultSeconds={DEFAULT_REST_SECONDS}
            />
          </>
        ) : null}

        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel}>Velocidade de execução</label>
          <SearchableSelect
            className={`${styles.fieldSelect}${isReadOnly ? ` ${styles.fieldInputDisabled}` : ''}`}
            value={localExecution.executionSpeed ?? ''}
            onChange={handleExecutionSpeedChange}
            options={EXERCISE_EXECUTION_SPEED_OPTIONS}
            placeholder="Selecionar velocidade"
            noResultsText="Nenhuma opção encontrada"
            readOnly={isReadOnly}
          />
        </div>

        {usesIsometrySpeed ? (
          <DurationPickerField
            id="exercise-execution-isometric-time"
            label="Tempo"
            value={localExecution.isometricSeconds ?? ''}
            onChange={(value) => handleSecondsFieldChange('isometricSeconds', value)}
            error={fieldErrors.isometricSeconds}
            placeholder="Configurar tempo"
            containerClassName={styles.durationField}
            maxMinutes={59}
            defaultSeconds={1}
          />
        ) : null}

        {usesCadenceSpeed ? (
          <div className={styles.cadenceTimesRow}>
            <DurationPickerField
              id="exercise-execution-concentric"
              label="Concêntrica"
              value={localExecution.concentricSeconds ?? ''}
              onChange={(value) => handleSecondsFieldChange('concentricSeconds', value)}
              error={fieldErrors.concentricSeconds}
              placeholder="Configurar tempo"
              containerClassName={styles.durationField}
              pickerMode="seconds"
              defaultSeconds={DEFAULT_CONCENTRIC_SECONDS}
              allowZero
            />
            <DurationPickerField
              id="exercise-execution-pause"
              label="Pausa"
              value={localExecution.pauseSeconds ?? ''}
              onChange={(value) => handleSecondsFieldChange('pauseSeconds', value)}
              error={fieldErrors.pauseSeconds}
              placeholder="Configurar tempo"
              containerClassName={styles.durationField}
              pickerMode="seconds"
              defaultSeconds={DEFAULT_PAUSE_SECONDS}
              allowZero
            />
            <DurationPickerField
              id="exercise-execution-eccentric"
              label="Excêntrica"
              value={localExecution.eccentricSeconds ?? ''}
              onChange={(value) => handleSecondsFieldChange('eccentricSeconds', value)}
              error={fieldErrors.eccentricSeconds}
              placeholder="Configurar tempo"
              containerClassName={styles.durationField}
              pickerMode="seconds"
              defaultSeconds={DEFAULT_ECCENTRIC_SECONDS}
              allowZero
            />
          </div>
        ) : null}

        {usesClusterFields ? (
          <>
            {(fieldErrors.repetitions || fieldErrors.roundRepetitions) ? (
              <div className={styles.errorList}>
                {fieldErrors.repetitions ? <span className={styles.errorText}>{fieldErrors.repetitions}</span> : null}
                {fieldErrors.roundRepetitions ? <span className={styles.errorText}>{fieldErrors.roundRepetitions}</span> : null}
              </div>
            ) : null}

            <div className={styles.pairedFieldsGrid}>
              <DurationPickerField
                id="exercise-execution-rest"
                label="Descanso"
                value={localExecution.rest}
                onChange={handleRestChange}
                error={fieldErrors.rest}
                placeholder="Configurar intervalo"
                containerClassName={styles.durationField}
                maxMinutes={59}
                defaultSeconds={DEFAULT_REST_SECONDS}
              />

              <DurationPickerField
                id="exercise-execution-intra-set-rest"
                label="Descanso intra série"
                value={localExecution.methodConfig?.intraSetRest ?? ''}
                onChange={(value) => handleClusterFieldChange('intraSetRest', value)}
                error={fieldErrors.intraSetRest}
                placeholder="Configurar intervalo"
                containerClassName={styles.durationField}
                maxMinutes={59}
                defaultSeconds={DEFAULT_REST_SECONDS}
              />
            </div>
          </>
        ) : null}

        {usesGvtFields ? (
          <span className={styles.helperText}>GVT fica fixo em 10 séries, 10 repetições e descanso entre 30 e 60 segundos.</span>
        ) : null}

        {usesDropFamilyDetails ? (
          <>
            <div className={styles.detailsSection}>
              <div className={styles.detailsSectionHeader}>
                <span className={styles.fieldLabel}>Execução por série</span>
              </div>

              <div className={styles.detailHeaderRow} style={{ gridTemplateColumns: dropDetailGridTemplateColumns }}>
                <span className={styles.detailHeaderCell}>Repetições</span>
                <span className={styles.detailHeaderCell}>% Carga</span>
                <span className={styles.detailHeaderCell}>Bloco</span>
                {usesPyramidDetails ? null : <span />}
              </div>

              <div className={styles.detailRows}>
                {localExecution.details.map((detail, index) => (
                  <div key={detail.id} className={styles.detailRow} style={{ gridTemplateColumns: dropDetailGridTemplateColumns }}>
                    <NumericStepperInput
                      size="detail"
                      value={detail.repetitionsMax || detail.repetitionsMin}
                      onChange={(value) => handleExecutionDetailChange(index, 'repetitionsMax', value)}
                      onKeyDown={handlePositiveIntegerKeyDown}
                      min={1}
                      max={999}
                      placeholder="12"
                      ariaLabel={`Repetições da série ${index + 1}`}
                      hasError={Boolean(fieldErrors.details)}
                    />
                    <NumericStepperInput
                      size="detail"
                      value={usesPyramidDetails
                        ? (getPyramidLoadPercentageDisplay(detail.repetitionsMax || detail.repetitionsMin) || detail.loadPercentage)
                        : detail.loadPercentage}
                      onChange={(value) => handleExecutionDetailChange(index, 'loadPercentage', value)}
                      onKeyDown={handlePositiveIntegerKeyDown}
                      min={0}
                      max={100}
                      placeholder="80"
                      ariaLabel={`Percentual de carga da série ${index + 1}`}
                      hasError={Boolean(fieldErrors.details)}
                      disabled={usesPyramidDetails}
                      readOnly={usesPyramidDetails}
                    />
                    <NumericStepperInput
                      size="detail"
                      value={detail.block}
                      onChange={(value) => handleExecutionDetailChange(index, 'block', value)}
                      onKeyDown={handlePositiveIntegerKeyDown}
                      min={1}
                      max={999}
                      placeholder="1"
                      ariaLabel={`Bloco da série ${index + 1}`}
                      hasError={Boolean(fieldErrors.details)}
                    />
                    {usesPyramidDetails ? null : (
                      <button
                        type="button"
                        className={styles.detailRemoveButton}
                        onClick={() => handleRemoveExecutionDetail(index)}
                        disabled={localExecution.details.length <= 1}
                        title={localExecution.details.length <= 1 ? 'Precisa de pelo menos uma série' : `Remover série ${index + 1}`}
                        aria-label={`Remover série ${index + 1}`}
                      >
                        <Icon name="delete" size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {fieldErrors.details ? (
                <div className={styles.errorList}>
                  <span className={styles.errorText}>{fieldErrors.details}</span>
                </div>
              ) : null}

              {usesPyramidDetails ? null : (
                <Button
                  type="button"
                  variant="outline"
                  size="small"
                  fullWidth
                  className={styles.detailsAddButton}
                  onClick={handleAddExecutionDetail}
                >
                  Adicionar
                </Button>
              )}
            </div>
          </>
        ) : null}
      </div>

      <div className={styles.footer}>
        <Button
          type="button"
          variant="primary"
          size="small"
          fullWidth
          onClick={isReadOnly ? onClose : handleConfirm}
        >
          {isReadOnly ? 'Fechar' : 'Aplicar'}
        </Button>
      </div>
    </div>
    <ExerciseVideoModal
      open={Boolean(videoTarget)}
      exerciseId={videoTarget?.catalogExerciseId ?? null}
      exerciseName={videoTarget?.name ?? ''}
      onClose={() => setVideoTarget(null)}
    />
    <ExerciseSwapModal
      open={Boolean(onReplaceExercise) && Boolean(swapTarget)}
      currentExerciseId={swapTarget?.catalogExerciseId ?? null}
      currentExerciseName={swapTarget?.name ?? ''}
      onCancel={() => setSwapTarget(null)}
      onConfirm={(nextExercise, replaceInEntirePlan) => {
        if (swapTarget) {
          onReplaceExercise?.(nextExercise, replaceInEntirePlan, {
            blockId: swapTarget.blockId,
            exerciseId: swapTarget.id,
          });
        }
        setSwapTarget(null);
      }}
    />
    </>,
    document.body,
  );
};

export default ExerciseExecutionEditor;